'use client';

// Imports
// *******************************
import { useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { Modal, Button } from '@components/common';
import { ModalSize } from '@type/modal-types';
import { SocialMediaShareList, SocialMediaShareListProps } from '@components/common/Elements/SocialMediaShareList';
import { useCopyToClipboard } from '@hooks/index';


// Modal Share interface
// *******************************
interface ShareModalProps extends Omit<SocialMediaShareListProps, 'shareUrl'> {
  onClose: () => void,
  isOpen: boolean,
  shareUrl?: string,
  pathnameShareUrl?: string,
  modalSize?: ModalSize,
  referralCode?: string,
}

export const ShareReferralModal = (props: ShareModalProps) => {
  // Props destructuring
  const {
    onClose,
    isOpen,
    shareUrl,
    pathnameShareUrl,
    modalSize = 'sm',
    referralCode,
    hashtag,
    ogTitle,
    ogDescription,
  } = props;

  // If we get a pathnameShareUrl, we'll use the current domain and append the pathnameShareUrl.
  const currentDomain = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrlWithPartial = pathnameShareUrl ? `${currentDomain}${pathnameShareUrl}` : '';

  // If we don't get a shareUrl, we'll use the current url without the search/query parameters.
  let currentUrl = typeof window !== 'undefined' ? window.location.href.split('?')[0] : '';

  if (referralCode) {
    currentUrl += `?referral_code=${referralCode}`;
  }

  // Get the final share url
  const finalShareUrl = shareUrl || shareUrlWithPartial || currentUrl || '';

  // Ref for input element
  const inputEl = useRef<HTMLInputElement>(null);

  // Hook for copying text to clipboard
  const { handleCopyText } = useCopyToClipboard(finalShareUrl);

  // Translation
  const { t } = useTranslation('common');

  // Copy text from input field
  const copyInputText = () => {
    handleCopyText();
    if (inputEl.current != null) {
      inputEl.current.select();
    }
  };

  // Modal
  return (
    <Modal isOpen={isOpen} onClose={onClose} header={t('job.detail.share-referral.tooltip')} size={modalSize}>
      <div className="p-6">
        <p className="text-center text-sm text-ink-medium">
          {t('job.detail.share-referral.description')}
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-2.5">
          <SocialMediaShareList
            shareUrl={finalShareUrl}
            hashtag={hashtag}
            ogTitle={ogTitle}
            ogDescription={ogDescription}
          />
        </div>

        <p className="mt-4 text-center text-sm text-ink-medium">
          {t('share.copy-link')}
        </p>

        <div className="mt-1.5 flex rounded-full border bg-surface-50 p-1">
          <input type="text" readOnly className="rounded-full !border-0 !shadow-none !outline-0 !ring-0" value={finalShareUrl} ref={inputEl} />
        </div>
        <div className="mt-3 text-center">
          <Button color="secondary" rounding="full" className="mx-auto" onClick={copyInputText}>{t('share.copy-label')}</Button>
        </div>
      </div>
    </Modal>
  );
};
